/**
 * Per-symbol SPINE catalog — maps each grid symbolId to its Spine skeleton under
 * `animations/spine/` (the shared master atlas) + how to seat it over the cell.
 *
 * The skeletons share one clip contract (animator-authored); every clip plays on the cell's one
 * skeleton, hosted by `SymbolSpineHost`:
 *   - `in`   — the LAND animation (`HostedSpineLandEffect`)
 *   - `win`  — the WIN HIGHLIGHT (`HostedSpineWinEffect`); chance's is its barrel explosion instead (the
 *     detonation plays it, and the barrel's cell leaves with it)
 *   - `out`  — the REMOVAL (`HostedSpineRemovalEffect` — replaces shrink+burst; absent on
 *     `chance` + `freespins_element`, which fall back to the legacy shrink+burst — except the
 *     detonating barrel, which leaves with its `win`)
 *   - `idle` (H/specials only) — ambient loop (the host's `idleClipName`, always-on while resting)
 *
 * `scale` is a fine-tune MULTIPLIER (spine art is authored 1:1 with the sprite natives — 1 matches).
 * Skeleton origins are authored centered, so offset defaults to 0.
 *
 * MAPPING VERIFIED (2026-09-01, visual): tiers run ASCENDING with the index —
 * H1 coin → H4 skull, L1 ten → L5 A; specials wild/chance/freespins_element.
 * (A wrong row is instantly visible — the spine art replaces the sprite the
 * moment the symbol lands. Edit HERE if symbols are ever re-ordered.)
 */
export interface SymbolSpineCatalogEntry {
  /** Logical symbol id (matches the server/fixture grid + the sprite catalog). */
  symbolId: string;
  /** Skeleton path inside `game-shared-visuals` (no extension suffix beyond `.skeleton`). */
  skeletonPath: string;
  /**
   * Fine-tune MULTIPLIER (default 1). The spine art is authored 1:1 with the sprite natives
   * (verified: compas 380≈379, rum 339×386≈337×384, freespins 444×376≈441×378), and sprite +
   * spine share the cell's content parent — so scale 1 IS the match. Override only for art quirks.
   */
  scale: number;
  /**
   * Whether the symbol rests on its `idle` loop (H/specials yes; the L card ranks carry none). The host
   * plays no idle for false, so the resting cell stays a plain sprite.
   */
  idle: boolean;
}

const DEFAULT_SCALE = 1; // spine art is authored 1:1 with the sprite natives — 1 is the match

export const SYMBOL_SPINE_CATALOG: readonly SymbolSpineCatalogEntry[] = [
  // High symbols — VERIFIED mapping (2026-09-01): coin→compass→rum→skull ascending.
  { symbolId: "H1", skeletonPath: "animations/spine/coin_element.skeleton", scale: DEFAULT_SCALE, idle: true },
  { symbolId: "H2", skeletonPath: "animations/spine/compas.skeleton", scale: DEFAULT_SCALE, idle: true },
  { symbolId: "H3", skeletonPath: "animations/spine/rum.skeleton", scale: DEFAULT_SCALE, idle: true },
  { symbolId: "H4", skeletonPath: "animations/spine/skull.skeleton", scale: DEFAULT_SCALE, idle: true },
  // Low symbols — VERIFIED mapping (2026-09-01): card ranks ascending ten→J→Q→K→A. No idle clips.
  { symbolId: "L1", skeletonPath: "animations/spine/ten.skeleton", scale: DEFAULT_SCALE, idle: false },
  { symbolId: "L2", skeletonPath: "animations/spine/j.skeleton", scale: DEFAULT_SCALE, idle: false },
  { symbolId: "L3", skeletonPath: "animations/spine/q.skeleton", scale: DEFAULT_SCALE, idle: false },
  { symbolId: "L4", skeletonPath: "animations/spine/K.skeleton", scale: DEFAULT_SCALE, idle: false },
  { symbolId: "L5", skeletonPath: "animations/spine/A.skeleton", scale: DEFAULT_SCALE, idle: false },
  // Specials — certain.
  { symbolId: "FS", skeletonPath: "animations/spine/freespins_element.skeleton", scale: DEFAULT_SCALE, idle: true },
  { symbolId: "CH", skeletonPath: "animations/spine/chance.skeleton", scale: DEFAULT_SCALE, idle: true },
  { symbolId: "W", skeletonPath: "animations/spine/wild.skeleton", scale: DEFAULT_SCALE, idle: true },
];

/** The land clip every symbol skeleton carries. */
export const SYMBOL_SPINE_LAND_CLIP = "in";

/**
 * Kill-switch for the Spine land animation (every symbol plays its `in` clip on land, on the view
 * the grid keeps, rolling on into its idle). false ⇒ symbols land plain (sprite-only).
 */
export const SYMBOL_SPINE_LAND_ENABLED = true;

/** The ambient idle loop the `idle: true` skeletons carry. */
export const SYMBOL_SPINE_IDLE_CLIP = "idle";

/** The WIN-highlight clip every symbol skeleton carries. (For `chance` — which has no `out` — it is
 *  the barrel explosion the detonation plays, ending with the symbol gone.) */
export const SYMBOL_SPINE_WIN_CLIP = "win";

/** The REMOVAL clip (the removal beat of `remove_symbols_by_mask` and of the chance detonation). Absent on
 *  `chance` + `freespins_element` — the removal effect finds no clip and returns null, so they fall back to the
 *  legacy shrink+burst; the detonating barrel instead leaves with its `win` (`ChanceRemovalEffect`). */
export const SYMBOL_SPINE_OUT_CLIP = "out";

/**
 * Crossfade length between symbol clips. Spine blends via per-instance `AnimationStateData`, and
 * every clip of a cell plays on its one hosted skeleton, so every ordered pair of the clips a
 * skeleton carries mixes: `in→idle`, `idle→win`, `win→idle` (the queued resume), `idle→out`,
 * `win→out`, … (the low ranks carry no `idle`; chance and freespins no `out`).
 * 0 disables mixing (every transition hard-cuts).
 */
export const SYMBOL_SPINE_MIX_SECONDS = 0.12;

/** The clip set the mix table spans — every symbol clip that can follow another on one skeleton. */
export const SYMBOL_SPINE_CLIP_MIX = {
  clipNames: [SYMBOL_SPINE_LAND_CLIP, SYMBOL_SPINE_IDLE_CLIP, SYMBOL_SPINE_WIN_CLIP, SYMBOL_SPINE_OUT_CLIP],
  seconds: SYMBOL_SPINE_MIX_SECONDS,
} as const;

/**
 * Kill-switch for the Spine REMOVAL animation (removed symbols play their `out` clip on the cell's
 * skeleton, REPLACING the shrink + spritesheet burst; symbols without the clip keep the legacy
 * removal). false ⇒ every cell shrinks + bursts.
 */
export const SYMBOL_SPINE_OUT_ENABLED = true;

/**
 * Kill-switch for the ambient idle overlays (resting H/W/CH/FS cells loop their `idle`
 * clip; sprite hidden under the spine). Independent of the land switch above.
 */
export const SYMBOL_SPINE_IDLE_ENABLED = true;

/**
 * Kill-switch for the Spine WIN animation (winners play their `win` clip on the cell's skeleton,
 * REPLACING the sprite pulse/sweep/wobble composite). false ⇒ the legacy sprite-based winner
 * composite is wired instead.
 */
export const SYMBOL_SPINE_WIN_ENABLED = true;
